// FILECONTENT_START src/common/components/Menu/Menu.tsx
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DefaultIcon from "@mui/icons-material/RadioButtonUnchecked";
import TreeItem, { treeItemClasses, TreeItemProps } from "@mui/lab/TreeItem";
import TreeView from "@mui/lab/TreeView";
import { Box, BoxProps, SvgIconProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import { ElementType } from "react";
import { Link, useLocation } from "react-router-dom";

export interface MenuItemProps {
  name: string;
  path: string;
  color?: string;
  bgColor?: string;
  info?: string | number;
  icon?: any; //ElementType<SvgIconProps<"svg", {}>>;
  items?: Array<MenuItemProps>;
  fullPath?: string;
  isGroup?: boolean;
  hidden?: boolean; // Hide from main menu but use in bradrumbs by route
}

declare module "react" {
  interface CSSProperties {
    "--tree-view-color"?: string;
    "--tree-view-bg-color"?: string;
  }
}

type StyledTreeItemProps = TreeItemProps & {
  bgColor?: string;
  color?: string;
  labelIcon: ElementType<SvgIconProps>;
  labelInfo?: string;
  to?: string;
  labelText: string;
};

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
  color: theme.palette.text.secondary,
  [`& .${treeItemClasses.content}`]: {
    color: theme.palette.text.secondary,
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    "&.Mui-expanded": {
      fontWeight: theme.typography.fontWeightRegular,
    },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused": {
      backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
      color: "var(--tree-view-color)",
    },
    [`& .${treeItemClasses.label}`]: {
      fontWeight: "inherit",
      color: "inherit",
    },
  },
  [`& .${treeItemClasses.group}`]: {
    marginLeft: 0,
    [`& .${treeItemClasses.content}`]: {
      paddingLeft: theme.spacing(2),
    },
  },
}));

interface ILinkProps {
  [id: string]: string;
}

interface LinkBoxProps extends BoxProps {
  to?: string;
}
function LinkBox({ to, ...props }: LinkBoxProps) {
  let linkProps: ILinkProps = {};
  if (to) {
    linkProps.to = to;
  }
  //@ts-ignore
  return <Box {...linkProps} component={to ? Link : undefined} {...props} />;
}

function StyledTreeItem(props: StyledTreeItemProps) {
  const {
    bgColor,
    color,
    labelIcon: LabelIcon,
    labelInfo,
    labelText,
    to,
    ...other
  } = props;

  return (
    <StyledTreeItemRoot
      label={
        <LinkBox
          to={to}
          sx={{
            display: "flex",
            alignItems: "center",
            p: 0.5,
            pr: 0,
            color: "inherit",
            textDecoration: "none",
          }}
        >
          <Box component={LabelIcon} color="inherit" sx={{ mr: 1 }} />
          <Typography
            variant="body2"
            sx={{ fontWeight: "inherit", flexGrow: 1 }}
          >
            {labelText}
          </Typography>
          <Typography variant="caption" color="inherit">
            {labelInfo}
          </Typography>
        </LinkBox>
      }
      style={{
        "--tree-view-color": color,
        "--tree-view-bg-color": bgColor,
      }}
      {...other}
    />
  );
}

StyledTreeItem.propTypes = {
  bgColor: PropTypes.string,
  color: PropTypes.string,
  labelIcon: PropTypes.elementType.isRequired,
  labelInfo: PropTypes.string,
  labelText: PropTypes.string.isRequired,
};

function getItems(items: Array<MenuItemProps>, parentPath?: string) {
  return items.map(item => {
    const path = `${parentPath || ""}${item.path}`;
    if (item.items) {
      return (
        <StyledTreeItem
          key={path}
          nodeId={path}
          labelText={item.name}
          labelIcon={item.icon || DefaultIcon}
          labelInfo={item.info ? String(item.info) : undefined}
          color={item.color}
          bgColor={item.bgColor}
        >
          {getItems(item.items, path)}
        </StyledTreeItem>
      );
    } else {
      return (
        <StyledTreeItem
          to={path}
          key={path}
          nodeId={path}
          labelText={item.name}
          labelIcon={item.icon || DefaultIcon}
          labelInfo={item.info ? String(item.info) : undefined}
          color={item.color}
          bgColor={item.bgColor}
        />
      );
    }
  });
}

export interface MenuProps {
  items: Array<MenuItemProps>;
}

export default function Menu({ items }: MenuProps) {
  const location = useLocation();

  return (
    <Box sx={{ overflow: "auto", paddingRight: 2, paddingTop: 2 }}>
      <TreeView
        aria-label="gmail"
        //defaultExpanded={["3"]}
        selected={location.pathname}
        defaultCollapseIcon={<ArrowDropDownIcon />}
        defaultExpandIcon={<ArrowRightIcon />}
        defaultEndIcon={<div style={{ width: 24 }} />}
        sx={{ flexGrow: 1, maxWidth: 400, overflowY: "auto" }}
      >
        {getItems(items)}
      </TreeView>
    </Box>
  );
}

// FILECONTENT_END src/common/components/Menu/Menu.tsx